import { useCallback, useEffect, useState } from 'react';
import { ChatSummary } from '../../domain';
import { formatDateTime } from '../../utils/date';

type SidebarProps = {
  open: boolean;
  chats: ChatSummary[];
  onSelectChat: (chatId: number) => void;
  onNewChat: () => void;
  onLogout: () => void;
  selectedChatId: number | null;
  showLogout: boolean;
  isDesktop: boolean;
  onClose: () => void;
  onOpen: () => void;
};

export function Sidebar({
  open,
  chats,
  onSelectChat,
  onNewChat,
  onLogout,
  selectedChatId,
  showLogout,
  isDesktop,
  onClose,
  onOpen,
}: SidebarProps) {
  const collapsedNav = !open && isDesktop;
  const [collapsedHoverCard, setCollapsedHoverCard] = useState<'new-chat' | 'history' | null>(null);
  const [toggleTooltip, setToggleTooltip] = useState<'collapse' | 'expand' | null>(null);

  useEffect(() => {
    if (!collapsedNav && collapsedHoverCard) {
      setCollapsedHoverCard(null);
    }
  }, [collapsedNav, collapsedHoverCard]);

  const collapsedButtonClasses =
    'flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-800 bg-slate-900 text-slate-400 transition hover:border-slate-700 hover:text-slate-100';
  const collapsedTooltipClasses =
    'absolute left-14 top-1/2 z-50 -translate-y-1/2 rounded-2xl border border-slate-800 bg-slate-950/95 p-4 text-left shadow-2xl';

  const handleCollapsedRailClick = useCallback(
    (event: React.MouseEvent) => {
      if (!collapsedNav) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      onOpen();
    },
    [collapsedNav, onOpen]
  );

  const fullSidebarContent = (
    <div className="flex h-full flex-col">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-sky-600 text-base font-semibold text-white">
            AI
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-100">Niro Chat</p>
            <p className="text-xs text-slate-400">Personal workbench</p>
          </div>
        </div>
        {!isDesktop && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Hide chat history"
            className="rounded-full border border-slate-800 bg-slate-950 p-2 text-slate-400 transition hover:border-slate-700 hover:text-slate-200"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {showLogout && (
          <button
            type="button"
            onClick={onLogout}
            className="rounded-full border border-slate-800 bg-slate-950 px-3 py-1 text-[11px] uppercase tracking-wide text-slate-400 transition hover:border-slate-700 hover:text-slate-200"
          >
            Sign out
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={onNewChat}
        className="mb-6 flex items-center justify-center gap-2 rounded-2xl border border-sky-600 bg-sky-600/10 px-4 py-3 text-sm font-semibold text-sky-200 transition hover:bg-sky-600/20"
      >
        <span className="text-lg">+</span>
        New chat
      </button>

      <div className="flex-1 overflow-y-auto pr-2">
        {chats.length === 0 ? (
          <p className="text-xs text-slate-500">No chats yet. Start a new conversation.</p>
        ) : (
          <ul className="space-y-1">
            {chats.map((chat) => (
              <li key={chat.id}>
                <button
                  type="button"
                  onClick={() => onSelectChat(chat.id)}
                  className={`w-full rounded-xl px-3 py-3 text-left text-sm transition ${
                    selectedChatId === chat.id
                      ? 'bg-slate-900 text-slate-100'
                      : 'text-slate-300 hover:bg-slate-900/70 hover:text-slate-100'
                  }`}
                >
                  <p className="truncate font-medium">{chat.title || 'Untitled chat'}</p>
                  <p className="mt-1 text-xs text-slate-500">{formatDateTime(chat.updated_at)}</p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {isDesktop && (
        <div className="mt-6 flex justify-center">
          <div
            className="relative"
            onMouseEnter={() => setToggleTooltip('collapse')}
            onMouseLeave={() => setToggleTooltip(null)}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Collapse sidebar"
              className={collapsedButtonClasses}
            >
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 6l-6 6 6 6" />
              </svg>
            </button>
            {toggleTooltip === 'collapse' && (
              <div className={`${collapsedTooltipClasses} min-w-[150px]`}>
                <p className="text-sm font-semibold text-slate-100">Collapse sidebar</p>
                <p className="text-xs text-slate-500">Hide the detailed navigation</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const collapsedSidebarContent = (
    <div className="flex h-full flex-col items-center justify-between py-4">
      <div className="flex flex-col items-center gap-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-600 text-sm font-semibold text-white">
          AI
        </div>
        <nav className="flex flex-col items-center gap-4">
          <div
            className="relative"
            onMouseEnter={() => setCollapsedHoverCard('new-chat')}
            onMouseLeave={() =>
              setCollapsedHoverCard((current) => (current === 'new-chat' ? null : current))
            }
          >
            <button
              type="button"
              onClick={() => {
                onNewChat();
              }}
              aria-label="Start a new chat"
              className={collapsedButtonClasses}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
              </svg>
            </button>
            {collapsedHoverCard === 'new-chat' && (
              <button
                type="button"
                onClick={() => {
                  onNewChat();
                  setCollapsedHoverCard(null);
                }}
                className={`${collapsedTooltipClasses} flex items-center gap-4`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-900/90">
                  <svg className="h-5 w-5 text-slate-100" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-sm font-semibold text-slate-100">New chat</p>
                  <p className="text-xs text-slate-500">Start a conversation</p>
                </div>
              </button>
            )}
          </div>

          <div
            className="relative"
            onMouseEnter={() => setCollapsedHoverCard('history')}
            onMouseLeave={() =>
              setCollapsedHoverCard((current) => (current === 'history' ? null : current))
            }
          >
            <button
              type="button"
              aria-label="Open chat history"
              className={collapsedButtonClasses}
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 1118 0 9 9 0 01-18 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 7v5l3 3" />
              </svg>
            </button>
            {collapsedHoverCard === 'history' && (
              <div className={`${collapsedTooltipClasses} w-64`}>
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">History</p>
                </div>
                <div className="mt-3 max-h-64 space-y-1 overflow-y-auto pr-1">
                  {chats.length === 0 ? (
                    <p className="text-xs text-slate-500">No chats yet.</p>
                  ) : (
                    chats.map((chat) => (
                      <button
                        key={chat.id}
                        type="button"
                        onClick={() => {
                          onSelectChat(chat.id);
                          setCollapsedHoverCard(null);
                        }}
                        className={`w-full rounded-xl px-3 py-2 text-left text-sm transition ${
                          selectedChatId === chat.id
                            ? 'bg-slate-900 text-slate-100'
                            : 'text-slate-300 hover:bg-slate-900/70 hover:text-slate-100'
                        }`}
                      >
                        <p className="truncate font-medium">{chat.title || 'Untitled chat'}</p>
                        <p className="text-xs text-slate-500">{formatDateTime(chat.updated_at)}</p>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </nav>
      </div>

      <div className="flex flex-col items-center gap-4">
        <div
          className="relative"
          onMouseEnter={() => setToggleTooltip('expand')}
          onMouseLeave={() => setToggleTooltip((current) => (current === 'expand' ? null : current))}
        >
          <button
            type="button"
            onClick={onOpen}
            aria-label="Expand sidebar"
            className={collapsedButtonClasses}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6l6 6-6 6" />
            </svg>
          </button>
          {toggleTooltip === 'expand' && (
            <div className={`${collapsedTooltipClasses} min-w-[150px]`}>
              <p className="text-sm font-semibold text-slate-100">Expand sidebar</p>
              <p className="text-xs text-slate-500">Show shortcuts and history</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {!isDesktop && open && (
        <div
          role="presentation"
          onClick={onClose}
          className="fixed inset-0 z-30 cursor-pointer bg-slate-950/60 backdrop-blur-sm"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-full max-w-xs flex-shrink-0 flex-col border-r border-slate-900 bg-slate-950/95 shadow-2xl transition-[width,transform] duration-300 ${
          open ? 'translate-x-0' : '-translate-x-full'
        } px-5 py-6 lg:relative lg:inset-auto lg:max-w-full lg:translate-x-0 lg:shadow-none ${
          open ? 'lg:w-72 lg:px-6 lg:py-6' : 'lg:w-16 lg:px-2 lg:py-4'
        }`}
        onClick={handleCollapsedRailClick}
        aria-hidden={!open && !isDesktop}
      >
        {isDesktop
          ? collapsedNav
            ? collapsedSidebarContent
            : fullSidebarContent
          : open
          ? fullSidebarContent
          : null}
      </aside>
    </>
  );
}
